import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPerformanceYear, getCurrentPeriod } from "../../../../../../../common/util/period";
import { useAppSelector } from "../../../../../../../store";
import LeafSelector, { OptionType } from "../../../../../../../common/leaf/LeafSelector/LeafSelector";
import { storedLocale } from "../../../../../../Authentication/util";
import TableTwoCols from "./TableTwoCols/TableTwoCols";
import {
  PerfHistoryData,
  PerfHistoryResponse,
} from "../../../../../../../services/PerformanceHistory/performanceHistoryAPI.types";
import LeafDateTime from "../../../../../../../common/leaf/LeafDateTime/LeafDateTime";
import LeafLineChart from "../../../../../../../common/leaf/LeafLineChart/LeafLineChart";
import { LeafErrorMessage } from "../../../../../../../common/leaf/LeafErrorMessage/LeafErrorMessage";
import { IconNoData } from "../../../../../../../common/icons/IconNoData/IconNoData";
import defaultContent from "../../../../../../../content/profile";
import { businessKpiClickTrack } from "../../../../../../../common/tealium/ABOProfile/Profile/ProfileTabViewsTrack/InsightsTabTrack";
import ABOProfilePerfHistoryKPIs from "./ABOProfilePerfHistoryKPIs/ABOProfilePerfHistoryKPIs";
import theme from "./ABOProfilePerfHistory.module.scss";
import "./ABOProfilePerfHistory.scss";

const ABOProfilePerfHistory = ({ showOnlyPerformanceHistory }: { showOnlyPerformanceHistory?: boolean }) => {
  const { t } = useTranslation(["profile"]);

  const locale = storedLocale();

  const affAbo = useAppSelector((state) => state.profile.affAbo);
  const { isoCountryCode } = useAppSelector((state) => state.boot?.user);
  const { perfHistory = [] } = useAppSelector((state) => state.boot?.configuration?.profile);
  const { selectedPeriod: currentSelectedPY } = useAppSelector((state) => state.period);
  const selectedPY = getPerformanceYear(currentSelectedPY);

  const KPIS_WITH_LABEL = perfHistory.map((kpi: { id: string; selected?: boolean }) => ({
    ...kpi,
    label: t(
      `aboProfile.aboProfilePerfHistory.${kpi.id}`,
      defaultContent["aboProfile"]["aboProfilePerfHistory"][kpi.id],
    ),
  })) as OptionType[];

  const getPerformanceYears = (date: Date, count: number) => {
    const startFrom = Number(getPerformanceYear(getCurrentPeriod()));
    return Array.from({ length: count }, (_, index) => {
      const py = startFrom - index;
      return {
        id: `${py}08`, // last month of the PY
        label: `${t(
          "aboProfile.aboProfilePerfHistory.performanceYear",
          defaultContent["aboProfile"]["aboProfilePerfHistory"]["performanceYear"],
        )} ${py}`,
        selected: `${py}` === selectedPY,
      };
    });
  };

  const [kpiOptions, setKPIOptions] = useState(KPIS_WITH_LABEL);
  const [pyOptions, setPYOptions] = useState(getPerformanceYears(new Date(), 3));
  const optionsRef = useRef<HTMLDivElement>(null);
  const savedScrollTopRef = useRef<number | null>(null);

  const getScrollablePanel = (): HTMLElement | null => {
    let el: HTMLElement | null = optionsRef.current?.parentElement ?? null;
    while (el) {
      const style = window.getComputedStyle(el);
      if ((style.overflowY === "auto" || style.overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  };

  const preserveScrollPosition = () => {
    const scrollParent = getScrollablePanel();
    savedScrollTopRef.current = scrollParent?.scrollTop ?? null;

    // Restore after MUI Select's internal focus management resets scroll
    setTimeout(() => {
      if (savedScrollTopRef.current !== null && scrollParent) {
        scrollParent.scrollTop = savedScrollTopRef.current;
        savedScrollTopRef.current = null;
      }
    }, 50);
  };

  const onSelectedKPIs = (option: OptionType) => {
    preserveScrollPosition();
    const updateKPIs = kpiOptions.map((kpi: OptionType) => ({
      ...kpi,
      selected: kpi.id === option.id,
    }));
    setKPIOptions(updateKPIs);
    businessKpiClickTrack(option.id, selectedPeriod.id);
  };

  const onSelectedPY = (option: OptionType) => {
    preserveScrollPosition();
    const updatePY = pyOptions.map((py: OptionType) => ({
      ...py,
      selected: py.id === option.id,
    }));
    setPYOptions(updatePY);
    businessKpiClickTrack(selectedKPI.id, option.id);
  };

  const formatTableDate = (data: PerfHistoryData) => {
    return <LeafDateTime date={data.isoDate} options={{ month: "long" }} ignoreTimeZone={true} />;
  };

  const formatTableKPIData = (data: PerfHistoryData) => {
    const currentPeriod = Number(getCurrentPeriod());
    const convertIsoToPeriod = (isoString: string) =>
      Number(`${isoString.substring(0, 4)}${isoString.substring(5, 7)}`);
    return convertIsoToPeriod(data.isoDate) <= currentPeriod ? data.decimalAmount || data.integerAmount : "--";
  };

  const formatChartDate = (date: number | string) => {
    const dateObject = new Date(date);
    const formatter = new Intl.DateTimeFormat(locale, {
      month: "narrow",
      year: void 0,
      timeZone: "UTC",
    });
    const formattedDate = formatter.format(dateObject);
    return formattedDate;
  };

  const formatChartData = (kpi: number | string) => `${kpi}`;

  const selectedKPI: OptionType = kpiOptions.find(({ selected }: OptionType) => selected === true) || kpiOptions[0];
  const selectedPeriod: OptionType = pyOptions.find(({ selected }: OptionType) => selected === true) || pyOptions[0];
  const selectedView = ABOProfilePerfHistoryKPIs.find((view) => view.id === selectedKPI.id);

  const getFilteredLineTrends = (data: PerfHistoryData[]) => {
    const currentPeriod = Number(getCurrentPeriod());
    const convertIsoToPeriod = (isoString: string) =>
      Number(`${isoString.substring(0, 4)}${isoString.substring(5, 7)}`);
    return data.filter((d) => convertIsoToPeriod(d.isoDate) <= currentPeriod);
  };
  return (
    <div className="abo-profile-perf-history">
      {showOnlyPerformanceHistory ? (
        <></>
      ) : (
        <h2 className="abo-profile-perf-history__title">{`${t(
          "aboProfile.aboProfilePerfHistory.title",
          defaultContent["aboProfile"]["aboProfilePerfHistory"]["title"],
        )}`}</h2>
      )}
      <div className="abo-profile-perf-history__content">
        <div className="abo-profile-perf-history__options" ref={optionsRef}>
          <div className="abo-profile-perf-history__options__py">
            <LeafSelector
              options={pyOptions}
              onSelected={onSelectedPY}
              ariaLabel={`${t(
                "aboProfile.aboProfilePerfHistory.pySelector",
                defaultContent["aboProfile"]["aboProfilePerfHistory"]["pySelector"],
              )}`}
            />
          </div>
          <div className="abo-profile-perf-history__options__kpis">
            <LeafSelector
              options={kpiOptions}
              onSelected={onSelectedKPIs}
              ariaLabel={`${t(
                "aboProfile.aboProfilePerfHistory.kpiSelector",
                defaultContent["aboProfile"]["aboProfilePerfHistory"]["kpiSelector"],
              )}`}
            />
          </div>
        </div>
        <div className="abo-profile-perf-history__content__data">
          {selectedView && (
            <selectedView.PerfHistoryData
              affAbo={affAbo}
              locale={locale}
              isoCountryCode={isoCountryCode}
              period={selectedPeriod.id}
            >
              {({ data, isFetching, isError, overrideFormatTableKPIData, overrideFormatChartData }) => {
                const noData = isFetching === false && (data?.annualTotal === 0 || isError);

                return (
                  <>
                    <div className={`abo-profile-perf-history__content__error--${noData && "show"}`}>
                      <LeafErrorMessage
                        show={noData}
                        icon={<IconNoData height="12.50rem" width="15.66rem" />}
                        headline={`${t(
                          "aboProfile.aboProfilePerfHistory.noData",
                          defaultContent["aboProfile"]["aboProfilePerfHistory"]["noData"],
                        )}`}
                        fontSize="big"
                        gap={true}
                        noContent={true}
                      />
                    </div>
                    <div className="abo-profile-perf-history__content__data__chart">
                      <LeafLineChart<PerfHistoryResponse, PerfHistoryData>
                        dataset={data ? [data] : []}
                        series={"data"}
                        x={"isoDate"}
                        y={"integerAmount"}
                        formatX={formatChartDate}
                        formatY={overrideFormatChartData || formatChartData}
                        staticTheme={theme.brunt_orange}
                        isFetching={isFetching}
                        showTooltips={false}
                        show={!noData}
                        filterFunction={getFilteredLineTrends}
                      />
                    </div>
                    <div className="abo-profile-perf-history__content__data__table">
                      <TableTwoCols<PerfHistoryData>
                        title={`${t(
                          `aboProfile.aboProfilePerfHistory.${selectedKPI.id}`,
                          defaultContent["aboProfile"]["aboProfilePerfHistory"][selectedKPI.id],
                        )}`}
                        list={data?.data}
                        col1={"isoDate"}
                        col2={"integerAmount"}
                        isFetching={isFetching}
                        formatCol1={formatTableDate}
                        formatCol2={overrideFormatTableKPIData || formatTableKPIData}
                        show={!noData}
                      />
                    </div>
                  </>
                );
              }}
            </selectedView.PerfHistoryData>
          )}
        </div>
      </div>
    </div>
  );
};

export default ABOProfilePerfHistory;
